import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Clock, BookOpen, Wine, Utensils, Users, Star, Calendar, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
export function Services() {
  const services = [{
    title: 'Private Wine Tastings',
    description: "Intimate, curated journeys through the world's finest vineyards, brought directly to your preferred setting.",
    features: ['Home Tastings', 'Office Tastings', 'Couples Experiences', 'Small Gatherings'],
    image: 'https://cdn.magicpatterns.com/uploads/gnejasSaeNaadmaXfm55uM/pexels-pavel-danilyuk-8775175.jpg'
  }, {
    title: 'Wine & Food Pairings',
    description: 'A symphony of flavors designed in collaboration with elite culinary partners.',
    features: ['Chef Collaborations', 'Restaurant Partnerships', 'Curated Tasting Menus', 'Educational Culinary Experiences'],
    image: 'https://cdn.magicpatterns.com/uploads/3uvfwg5QvVCTREXacnu23e/pexels-buselliyy-33144656.jpg'
  }, {
    title: 'Wine Cocktail Classes',
    description: 'Interactive and entertainment-focused sessions exploring the art of the wine-based cocktail.',
    features: ['Interactive Experiences', 'Signature Cocktails', 'Entertainment-focused', 'Modern Mixology'],
    image: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?auto=format&fit=crop&q=80&w=800'
  }, {
    title: 'Corporate Experiences',
    description: 'Sophisticated programming tailored for professional networking and high-level hospitality.',
    features: ['Networking Events', 'Client Appreciation', 'Government Functions', 'Hospitality Events'],
    image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&q=80&w=800'
  }];
  const processSteps = [{
    title: 'Theme Selection',
    desc: 'We begin by selecting a theme-based wine portfolio tailored to your group.'
  }, {
    title: 'Educational Intro',
    desc: 'A brief, engaging presentation on the regions and varietals featured.'
  }, {
    title: 'Guided Tasting',
    desc: 'A sensory journey through each selection, led by our expert hosts.'
  }, {
    title: 'Pairing Discussion',
    desc: 'Interactive dialogue on flavor profiles and culinary harmonies.'
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} className="min-h-screen bg-white" data-id="element-522">
      <Header data-id="element-523" />

      <main className="pt-32 pb-24" data-id="element-524">
        {/* Intro */}
        <section className="max-w-7xl mx-auto px-6 mb-32 text-center" data-id="element-525">
          <span className="section-overline" data-id="element-526">Our Offerings</span>
          <h1 className="text-[#4A0E0E] mb-8" data-id="element-527">Services & Experiences</h1>
          <p className="text-[#2C2C2C]/60 max-w-3xl mx-auto" data-id="element-528">
            Cork Dorx specializes in creating approachable yet deeply
            sophisticated wine events. From intimate home gatherings to
            large-scale corporate functions, we curate every detail to ensure an
            unforgettable cultural experience.
          </p>
        </section>

        {/* Service Cards */}
        <section className="max-w-7xl mx-auto px-6 mb-48" data-id="element-529">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-530">
            {services.map((service, idx) => <div key={idx} className="group bg-[#FDFCF0] rounded-3xl overflow-hidden flex flex-col" data-id="element-531">
                <div className="h-80 overflow-hidden" data-id="element-532">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-533" />
                </div>
                <div className="p-12 flex-1 flex flex-col" data-id="element-534">
                  <h3 className="text-3xl font-serif text-[#4A0E0E] mb-6" data-id="element-535">
                    {service.title}
                  </h3>
                  <p className="text-[#2C2C2C]/70 mb-8 font-light leading-relaxed" data-id="element-536">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-10" data-id="element-537">
                    {service.features.map((f, i) => <li key={i} className="flex items-center gap-3 text-sm text-[#2C2C2C]/80" data-id="element-538">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" data-id="element-539" />
                        {f}
                      </li>)}
                  </ul>
                  <button className="mt-auto flex items-center gap-2 text-[#4A0E0E] font-bold uppercase tracking-widest text-xs group-hover:text-[#D4AF37] transition-colors" data-id="element-540">
                    Inquire About This Service <ChevronRight size={16} data-id="element-541" />
                  </button>
                </div>
              </div>)}
          </div>
        </section>

        {/* Event Structure */}
        <section className="bg-[#4A0E0E] text-white py-32 px-6 mb-48" data-id="element-542">
          <div className="max-w-7xl mx-auto" data-id="element-543">
            <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8" data-id="element-544">
              <div className="max-w-2xl" data-id="element-545">
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-6 block" data-id="element-546">
                  The Process
                </span>
                <h2 className="text-4xl md:text-6xl font-serif" data-id="element-547">
                  A 2-Hour Sensory Journey
                </h2>
              </div>
              <div className="flex items-center gap-4 text-[#D4AF37]" data-id="element-548">
                <Clock size={24} data-id="element-549" />
                <span className="text-lg font-serif italic" data-id="element-550">
                  Approximately 120 Minutes
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12" data-id="element-551">
              {processSteps.map((step, idx) => <div key={idx} className="relative" data-id="element-552">
                  <div className="text-6xl font-serif text-white/10 absolute -top-8 -left-4" data-id="element-553">
                    {idx + 1}
                  </div>
                  <h4 className="text-xl font-serif mb-4 relative z-10" data-id="element-554">
                    {step.title}
                  </h4>
                  <p className="text-white/60 text-sm font-light leading-relaxed" data-id="element-555">
                    {step.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </section>

        {/* Capacity & Staffing */}
        <section className="max-w-7xl mx-auto px-6 mb-48" data-id="element-556">
          <div className="text-center mb-20" data-id="element-557">
            <h2 className="text-4xl font-serif text-[#4A0E0E] mb-6" data-id="element-558">
              Event Capacity & Staffing
            </h2>
            <p className="text-[#2C2C2C]/60 font-light" data-id="element-559">
              Operational excellence tailored to your event size.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-560">
            {[{
            size: 'Small Events',
            staff: 'Independent Management',
            desc: 'Perfect for intimate home tastings and small executive gatherings.'
          }, {
            size: 'Medium Events',
            staff: 'Additional Pourers',
            desc: 'Ideal for social mixers and boutique corporate functions.'
          }, {
            size: 'Large Events',
            staff: 'Multiple Staff + Media',
            desc: 'Full-scale hospitality management for galas and major brand events.'
          }].map((item, idx) => <div key={idx} className="border border-[#E6D5B8] p-10 rounded-2xl text-center hover:bg-[#FDFCF0] transition-colors" data-id="element-561">
                <h4 className="text-xl font-serif text-[#4A0E0E] mb-2" data-id="element-562">
                  {item.size}
                </h4>
                <div className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-6" data-id="element-563">
                  {item.staff}
                </div>
                <p className="text-[#2C2C2C]/60 text-sm font-light" data-id="element-564">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </section>

        {/* Booking CTA */}
        <section className="max-w-5xl mx-auto px-6" data-id="element-565">
          <div className="bg-[#FDFCF0] rounded-[3rem] p-12 md:p-24 border border-[#E6D5B8]/50 text-center" data-id="element-566">
            <h2 className="text-4xl md:text-6xl font-serif text-[#4A0E0E] mb-8" data-id="element-567">
              Request a Consultation
            </h2>
            <p className="text-lg text-[#2C2C2C]/70 mb-12 font-light max-w-2xl mx-auto" data-id="element-568">
              Ready to elevate your next event? Share a few details with us and
              we’ll prepare a bespoke quote tailored to your vision.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left mb-12 max-w-2xl mx-auto" data-id="element-569">
              <div className="space-y-2" data-id="element-570">
                <label className="text-[10px] uppercase tracking-widest font-bold text-[#4A0E0E]" data-id="element-571">
                  Event Type
                </label>
                <select className="w-full bg-white border border-[#E6D5B8] rounded-lg px-4 py-3 text-sm focus:ring-1 focus:ring-[#4A0E0E] outline-none" data-id="element-572">
                  <option data-id="element-573">Private Tasting</option>
                  <option data-id="element-574">Corporate Event</option>
                  <option data-id="element-575">Culinary Pairing</option>
                </select>
              </div>
              <div className="space-y-2" data-id="element-576">
                <label className="text-[10px] uppercase tracking-widest font-bold text-[#4A0E0E]" data-id="element-577">
                  Estimated Guests
                </label>
                <input type="number" placeholder="e.g. 12" className="w-full bg-white border border-[#E6D5B8] rounded-lg px-4 py-3 text-sm focus:ring-1 focus:ring-[#4A0E0E] outline-none" data-id="element-578" />
              </div>
            </div>
            <button className="bg-[#4A0E0E] text-white px-12 py-5 rounded-full font-bold uppercase tracking-widest text-sm hover:bg-[#D4AF37] transition-all duration-500 shadow-xl" data-id="element-579">
              Request Event Quote
            </button>
          </div>
        </section>
      </main>

      <Footer data-id="element-580" />
    </motion.div>;
}